import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Layout, Row, Col, theme, App, Alert, Flex, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import Header from '../../widgets/header/Header';
import Calculator from '../../widgets/calculator/Calculator';
import type { CalculatorData } from '../../widgets/calculator/Calculator';
import CalculatorSummary from '../../widgets/calculator/CalculatorSummary';
import BsrChart from '../../widgets/forecast/BsrChart';
import SalesPlan from '../../widgets/forecast/SalesPlan';
import type { SalesPlanData } from '../../widgets/forecast/SalesPlan';
import Budget from '../../widgets/budget/Budget';
import type { BudgetCategory } from '../../widgets/budget/Budget';
import ForecastAmazonUS from '../../widgets/forecast/ForecastAmazonUS';
import type { ForecastSummaryData } from '../../widgets/forecast/ForecastAmazonUS';
import Summary from '../../widgets/summary/Summary';
import Notes from '../../widgets/notes/Notes';
import CompetitorAnalysis from '../../widgets/competitors/CompetitorAnalysis';
import ProductHistory from '../../widgets/product-history/ProductHistory';
import UserHistoryBar from '../../widgets/user-history/UserHistoryBar';
import {
  loadSavedResult,
  loadProductCalculation,
  fetchSharedResult,
  fetchProductCard,
  type ProductCardFields,
  type ProductCalculationDetail,
} from '../../shared/api/client';
import { SHARE_HANDOFF_KEY, clearShareHandoff } from '../../shared/session/shareHandoff';
import { hostLocationWithoutShare, readShareIdFromHostLocation } from '../../shared/lib/platformShareLink';
import { mapSalesPlan, mapCalcData, productCalculationPayloadToLoadedResult } from '../../shared/lib/resultMapping';
import { sumBudget, computeCogs, computeFullUnitCost, LAUNCH_BUDGET_DEFAULT } from '../../shared/lib/budget';
import { buildBackendPayload } from '../../shared/lib/buildBackendPayload';
import type { ProductEntry } from '../../shared/lib/productEntry';

interface DashboardViewProps {
  // В embed свой Header не рендерится: хром (навигация, пользователь, язык)
  // даёт платформа, а персональные действия шапки — сохранение, шаринг, экспорт —
  // ждут платформенной авторизации из S2.
  withHeader?: boolean;
  // Товар, с которым платформа открыла модуль. Есть только в embed — в
  // standalone своей карточки платформы нет, App её и не передаёт.
  productEntry?: ProductEntry | null;
}

const DashboardView: React.FC<DashboardViewProps> = ({ withHeader = true, productEntry }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [calculatorData, setCalculatorData] = useState<CalculatorData | undefined>();
  const [productCard, setProductCard] = useState<ProductCardFields | null>(null);
  const [salesPlanData, setSalesPlanData] = useState<SalesPlanData | undefined>();
  const [budgetData, setBudgetData] = useState<BudgetCategory[] | undefined>();
  const [forecastSummary, setForecastSummary] = useState<ForecastSummaryData | null>(null);
  const [asin, setAsin] = useState('');
  const [competitorAsins, setCompetitorAsins] = useState<string[]>([]);
  const [loadedResult, setLoadedResult] = useState<Record<string, unknown> | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [loadedFileName, setLoadedFileName] = useState('');
  const [notesData, setNotesData] = useState<string>('');

  /**
   * Расчёт по шар-ссылке грузится так же, как из истории: данные + имя в поле
   * названия. Имя — из самой записи: получателю оно нужно, чтобы одним «Сохранить»
   * завести свою копию под тем же названием (имена разных владельцев не спорят,
   * миграция 008). Без имени копия была бы «безымянной» до ручного ввода.
   */
  const applySharedResult = useCallback((data: Record<string, unknown>) => {
    setLoadedResult(data);
    setLoadKey((k) => k + 1);
    setLoadedFileName(typeof data.fileName === 'string' ? data.fileName : '');
  }, []);
  const handleLoadProductCalculation = useCallback((detail: ProductCalculationDetail) => {
    setLoadedResult(productCalculationPayloadToLoadedResult(detail.payload));
    setLoadKey((k) => k + 1);
  }, []);
  const reportSharedLoadFailure = useCallback(
    () => message.error(t('idea_calc.messages.load_shared_error')),
    [message, t],
  );

  useEffect(() => {
    // Ключ пишет ТОЛЬКО маршрут `/s/:shareId`, а он существует ровно в том дереве
    // маршрутов, где рендерится наш Header (`App.tsx: createStandaloneMode`). В embed
    // писателя нет вовсе, зато sessionStorage там общий со страницей платформы:
    // читать и стирать в нём ключ значило бы трогать чужое хранилище без единого
    // шанса что-то в нём найти. Второй признак «мы в embed» здесь не заводится
    // (запрет из App.tsx) — берётся тот же проп, которым режимы уже различены.
    if (!withHeader) return;
    const shareId = sessionStorage.getItem(SHARE_HANDOFF_KEY);
    if (shareId) {
      clearShareHandoff();
      fetchSharedResult(shareId).then(applySharedResult).catch(reportSharedLoadFailure);
    }
  }, [withHeader, applySharedResult, reportSharedLoadFailure]);

  // Хост назвал конкретный расчёт — показываем его, а не новый лист по карточке
  // товара. Ключ из примитивов: платформа шлёт новый объект контекста на смену темы.
  const linkedCalculation = productEntry?.calculation ?? null;
  const linkedCalculationKey =
    linkedCalculation === null
      ? null
      : linkedCalculation.kind === 'share'
        ? `share:${linkedCalculation.shareId}`
        : linkedCalculation.kind === 'history'
          ? `history:${linkedCalculation.calculationId}`
          : `invalid:${linkedCalculation.reason}`;
  // Ключ расчёта, который не загрузился. Хранится ключ, а не флаг: сменился расчёт — старый
  // отказ к нему не относится, и сбрасывать флаг отдельным setState в эффекте не нужно.
  const [failedLinkedKey, setFailedLinkedKey] = useState<string | null>(null);
  const linkedFailed =
    linkedCalculation?.kind === 'invalid' || (linkedCalculationKey !== null && failedLinkedKey === linkedCalculationKey);

  useEffect(() => {
    if (!productEntry || linkedCalculation === null) return;
    let cancelled = false;
    if (linkedCalculation.kind === 'invalid') {
      message.error(t('idea_calc.messages.load_calc_error'));
      return;
    }
    const load =
      linkedCalculation.kind === 'share'
        ? fetchSharedResult(linkedCalculation.shareId).then((data) => {
            if (!cancelled) applySharedResult(data);
          })
        : loadProductCalculation(productEntry.productId, linkedCalculation.calculationId).then((detail) => {
            if (!cancelled) handleLoadProductCalculation(detail);
          });
    load.catch(() => {
      if (cancelled) return;
      setFailedLinkedKey(linkedCalculationKey);
      message.error(
        t(linkedCalculation.kind === 'share' ? 'idea_calc.messages.load_shared_error' : 'idea_calc.messages.load_calc_error'),
      );
    });
    return () => {
      cancelled = true;
    };
    // Зависимости — id товара и ключ расчёта, не сами объекты (см. комментарий выше).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productEntry?.productId, linkedCalculationKey]);

  useEffect(() => {
    // Названный расчёт открывается как есть: подставить поверх него карточку товара
    // значило бы показать не тот просчёт, который к лаунчу привязан (WS 15556998).
    // Не загрузился — об этом сказано ошибкой, а не подменой листом по карточке.
    if (!productEntry || productEntry.calculation !== null) return;
    let cancelled = false;
    fetchProductCard(productEntry.productId)
      .then((card) => {
        if (!cancelled) setProductCard(card);
      })
      .catch(() => {
        if (!cancelled) message.error(t('idea_calc.messages.load_product_card_error'));
      });
    return () => {
      cancelled = true;
    };
    // Зависимость — id, не сам `productEntry`: платформа шлёт новый объект
    // контекста при смене темы (см. App.tsx), а карточка привязана к товару,
    // не к идентичности объекта.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productEntry?.productId, linkedCalculationKey]);

  useEffect(() => {
    // Ссылка «Поделиться» из платформы: `/apps/profitCalculator?share=<id>` (или с id
    // в подпути — когда платформа заведёт wildcard). Адрес видим только у хоста, и
    // читается он ровно в том монтировании, куда такая ссылка ведёт: standalone-вью
    // в embed (нет ни карточки товара, ни своего Header). id из адреса убирается
    // сразу, иначе «Обновить страницу» перезатёр бы правки получателя расчётом.
    if (productEntry || withHeader) return;
    const shareId = readShareIdFromHostLocation(window.location);
    if (!shareId) return;
    window.history.replaceState(window.history.state, '', hostLocationWithoutShare(window.location));
    fetchSharedResult(shareId).then(applySharedResult).catch(reportSharedLoadFailure);
  }, [productEntry, withHeader, applySharedResult, reportSharedLoadFailure]);

  const handleCalcChange = useCallback((data: CalculatorData) => {
    setCalculatorData(data);
    if (data.asin) setAsin(data.asin);
    else setAsin('');
    if (data.competitorAsins?.length) setCompetitorAsins(data.competitorAsins);
  }, []);

  const handleLoadResult = useCallback(async (fileName: string) => {
    try {
      const data = await loadSavedResult(fileName + '.json');
      setLoadedResult(data);
      setLoadKey((k) => k + 1);
      setLoadedFileName(fileName);
    } catch {
      message.error(t('idea_calc.messages.load_calc_error'));
    }
  }, []);

  // Payload карточки товара собирается по клику в ProductHistory, а не на каждое
  // изменение калькулятора — функция, а не мемоизированный объект.
  const buildProductHistoryPayload = useCallback(
    () => buildBackendPayload(calculatorData, salesPlanData, budgetData, notesData),
    [calculatorData, salesPlanData, budgetData, notesData],
  );

  const calcInitial = useMemo(
    () => loadedResult ? mapCalcData(loadedResult.calculation_data as Record<string, unknown>) : undefined,
    [loadedResult],
  );
  const budgetInitial = useMemo(() => loadedResult?.budget_data as any, [loadedResult]);
  const salesPlanInitial = useMemo(() => mapSalesPlan(loadedResult?.sales_plan), [loadedResult]);
  const notesInitial = useMemo(() => loadedResult?.notes as string | undefined, [loadedResult]);

  // Calculate total budget from budget categories (matching Budget.tsx logic)
  const totalLaunchBudget = useMemo(() => {
    if (!budgetData) return LAUNCH_BUDGET_DEFAULT;
    const cogs = calculatorData ? computeCogs(calculatorData) : 5;
    const fullUnitCost = calculatorData ? computeFullUnitCost(calculatorData) : 0;
    return sumBudget(budgetData, cogs, fullUnitCost);
  }, [budgetData, calculatorData]);

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      {withHeader && (
        <Header calculatorData={calculatorData} salesPlanData={salesPlanData} budgetData={budgetData} notesData={notesData} onLoadResult={handleLoadResult} onLoadShared={applySharedResult} loadedFileName={loadedFileName} />
      )}
      <Layout.Content
        style={{
          maxWidth: 1720,
          width: '100%',
          margin: '0 auto',
          padding: token.padding,
        }}
      >
        {/* position: relative — точка отсчёта для сайдбара истории: он рендерится
            без портала, чтобы не вылезать за пределы слота модуля на странице
            платформы (см. ProductHistory). */}
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {productEntry && (
            <ProductHistory
              productId={productEntry.productId}
              buildPayload={buildProductHistoryPayload}
              onLoad={handleLoadProductCalculation}
            />
          )}
          {/* Монтирование без карточки товара (`/apps/profitCalculator`, `entry`
              есть, `productEntry` пуст): своего Header тут нет (`withHeader=false`),
              а без него сохранение и история были бы недостижимы вовсе. В
              standalone эти же действия уже живут в Header — вторая строка
              там лишняя. */}
          {!productEntry && !withHeader && (
            <UserHistoryBar
              buildPayload={buildProductHistoryPayload}
              onLoad={handleLoadResult}
              onLoadShared={applySharedResult}
              loadedFileName={loadedFileName}
            />
          )}
          {/* Пока названный хостом расчёт не пришёл, листа нет: калькулятор без данных рисует
              заводские числа (35.59 / 8.36), и их принимали за привязанный просчёт (QA stage
              24.09). Выбрать другой расчёт можно в «Истории» выше — он и откроет лист. */}
          {linkedCalculation !== null && loadedResult === null ? (
            linkedFailed ? (
              <Alert
                type="error"
                showIcon
                data-testid="linked-calc-unavailable"
                message={t('idea_calc.messages.linked_calc_unavailable')}
              />
            ) : (
              <Flex justify="center" align="center" gap="small" style={{ padding: token.paddingLG }} data-testid="linked-calc-loading">
                <Spin />
                {t('idea_calc.messages.linked_calc_loading')}
              </Flex>
            )
          ) : (
            <>
              <Row gutter={[16, 16]} id="calculator" align="stretch">
                <Col xs={24} lg={6} style={{ display: 'flex' }}>
                  {/* Загруженный расчёт (история, ссылка) показывается своими числами:
                      перемонтированный по loadKey калькулятор иначе снова подставил бы
                      поверх него поля карточки товара. */}
                  <Calculator key={`calc-${loadKey}`} onDataChange={handleCalcChange} initialData={calcInitial} productCard={loadedResult ? null : productCard} />
                </Col>
                <Col xs={24} lg={10} id="budget" style={{ display: 'flex' }}>
                  <Budget
                    key={`budget-${loadKey}`}
                    cogs={calculatorData ? computeCogs(calculatorData) : undefined}
                    fullUnitCost={calculatorData ? computeFullUnitCost(calculatorData) : undefined}
                    initialData={budgetInitial}
                    onDataChange={setBudgetData}
                  />
                </Col>
                <Col xs={24} lg={8} style={{ display: 'flex' }}>
                  <CalculatorSummary data={calculatorData} launchBudget={totalLaunchBudget} couponPromoEnabled={calculatorData?.couponPromoEnabled} />
                </Col>
              </Row>
              {/* По одному ASIN, как в старом туле: расчёт из истории или по ссылке несёт ASIN,
                  но не признак «загружено из Keepa» — с ним график у загруженных расчётов
                  не появлялся вовсе. */}
              {asin && <BsrChart asin={asin} />}
              {(asin || competitorAsins.length > 0) && (
                <div id="competitor-analysis">
                  <CompetitorAnalysis
                    mainAsin={asin}
                    competitorAsins={competitorAsins}
                    ourPrice={calculatorData?.sellingPrice}
                    ourCogs={calculatorData ? calculatorData.purchasePrice + calculatorData.shippingPrice : 0}
                  />
                </div>
              )}
              <div id="sales-plan">
                <SalesPlan
                  key={`sp-${loadKey}`}
                  onDataChange={setSalesPlanData}
                  asin={asin}
                  competitorAsins={competitorAsins}
                  purchasePrice={calculatorData?.purchasePrice}
                  initialData={salesPlanInitial}
                />
              </div>
              <div id="forecast">
                <ForecastAmazonUS
                  calculatorData={calculatorData}
                  salesPlanData={salesPlanData}
                  launchBudget={totalLaunchBudget}
                  onSummaryChange={setForecastSummary}
                />
              </div>
              <div id="summary">
                <Summary forecastSummary={forecastSummary} />
              </div>
              <div id="notes">
                <Notes key={`notes-${loadKey}`} initialNotes={notesInitial} onDataChange={setNotesData} />
              </div>
            </>
          )}
        </div>
      </Layout.Content>
    </Layout>
  );
};

export default DashboardView;
